<template>
  <div class="flex w-full min-w-0 items-start gap-3 px-4 phone:gap-3 phone:pl-4 phone:pr-0">
    <wui-btn
      v-if="!isEditing"
      class="mt-2 hidden size-[50px]! shrink-0 rounded-8 bg-bg-base-bg4! phone:inline-flex"
      variant="neut"
      state="alpha"
      :size="48"
      data-test="uploading-file-list-delete-all-files-from-room-btn"
      icon
      prepend-icon="trashM"
      @click="deleteAllFilesFromRoom()"
    >
      <span class="sr-only select-none">{{ $t('common.removeFile') }}</span>
    </wui-btn>
    <wui-btn
      v-if="isEditing"
      class="mt-2 hidden size-[50px]! shrink-0 rounded-8 bg-bg-base-bg4! phone:inline-flex"
      variant="neut"
      state="alpha"
      :size="48"
      data-test="uploading-file-list-delete-all-btn"
      icon
      prepend-icon="trashM"
      @click="emit('deleteAll')"
    />

    <div
      class="
        uploading-file-list-scroll
        flex min-w-0 flex-1 flex-wrap content-start items-start gap-3
        pt-2 pr-2
        phone:flex-nowrap phone:gap-3 phone:overflow-x-auto
        will-change-scroll
      "
    >
      <template v-if="isEditing">
        <editing-file-item
          v-for="file in filesToEdit"
          :key="file.filecookie"
          :file="file"
          @delete="emit('delete', file.filecookie)"
        />
        <uploading-file-item
          v-for="(file, index) in editingFilesToUpload"
          :key="`editing-upload-${index}`"
          :index="index"
          :file="file"
          @delete="onDeleteEditingFileToUpload(file)"
        />
      </template>
      <uploading-file-item
        v-for="(file, index) in files"
        :key="`upload-${index}`"
        data-test="uploading-file-item"
        :index="index"
        :file="file"
        @delete="deleteFileFromRoom(file)"
      />

      <wui-btn
        v-if="!isEditing"
        class="size-[50px]! shrink-0 rounded-8 bg-bg-base-bg4! phone:hidden"
        variant="neut"
        state="alpha"
        :size="48"
        data-test="uploading-file-list-delete-all-files-from-room-btn"
        icon
        prepend-icon="trashM"
        @click="deleteAllFilesFromRoom()"
      >
        <span class="sr-only select-none">{{ $t('common.removeFile') }}</span>
      </wui-btn>
      <wui-btn
        v-if="isEditing"
        class="size-[50px]! shrink-0 rounded-8 bg-bg-base-bg4! phone:hidden"
        variant="neut"
        state="alpha"
        :size="48"
        data-test="uploading-file-list-delete-all-btn"
        icon
        prepend-icon="trashM"
        @click="emit('deleteAll')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WuiBtn } from '@wui/common-library'
import { IMFile } from '@wui/im'
import { computed, onBeforeUnmount, watch, toRefs } from 'vue'



import { draftModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'

import { usePromptServer } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'

import EditingFileItem from '../footer/editing-file-item/editing-file-item.vue'

import UploadingFileItem from './uploading-file-item.vue'

const props = defineProps<{
  files: ChatFile[],
  roomId: string,
  filesToEdit?: IMFile[] | null,
  editingFilesToUpload?: ChatFile[],
  isEditing?: boolean,
}>()

const emit = defineEmits<{
  (event: 'delete', filecookie: string): void;
  (event: 'deleteAll'): void;
  (event: 'delete-editing-file-to-upload', file: ChatFile): void
}>()

const { isEditing, files, editingFilesToUpload } = toRefs(props)

const storeRooms = useStoreRooms()
const roomPserved = computed(() => storeRooms.getRoomById(props.roomId)?.pServedUser)

const { deleteDraftFile } = draftModel.useDraftMessageStore()

const { abortSaveFile, removeFile } = usePromptServer()

const deleteFileFromRoom = (file: ChatFile) => {
  if (roomPserved.value) {
    deleteDraftFile(roomPserved.value, file)
  }

  if (file.isLoaded) {
    removeFile(file.name)
  } else {
    abortSaveFile(file.file)
  }
}

const deleteAllFilesFromRoom = () => {
  files.value.forEach(f => {
    deleteFileFromRoom(f)
  })
  files.value.filter(f => !f.isLoaded.value).forEach(file => abortSaveFile(file.file))
}

const onDeleteEditingFileToUpload = (file: ChatFile) => {
  emit('delete-editing-file-to-upload', file)

  abortSaveFile(file.file)
}

watch(isEditing, (v) => {
  if (v && files.value.length > 0) {
    deleteAllFilesFromRoom()
  }
})

onBeforeUnmount(() => {
  files.value.filter(f => !f.isLoaded.value).forEach(file => abortSaveFile(file.file))
  editingFilesToUpload.value?.filter(f => !f.isLoaded.value).forEach(file => abortSaveFile(file.file))
})

</script>

<style scoped>
@media (max-width: 501px) {
  .uploading-file-list-scroll {
    scrollbar-width: none;
  }

  .uploading-file-list-scroll::-webkit-scrollbar {
    display: none;
  }
}
</style>
